import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { isApplePlatform } from '@/utils/mobileOsDetection';
import { typography } from '@/styles/tokens';

interface AppleSignInButtonProps {
  onPress: () => void;
  disabled?: boolean;
  style?: ViewStyle;
}

const webDataAttrs = Platform.OS === 'web'
  ? ({ dataSet: { authButton: 'apple' } } as unknown as { [key: string]: unknown })
  : {};

const webLabelAttrs = Platform.OS === 'web'
  ? ({ dataSet: { authButtonLabel: 'true' } } as unknown as { [key: string]: unknown })
  : {};

const webIconAttrs = Platform.OS === 'web'
  ? ({ dataSet: { authButtonIcon: 'true' } } as unknown as { [key: string]: unknown })
  : {};

export function AppleSignInButton({ onPress, disabled, style }: AppleSignInButtonProps) {
  const { t } = useTranslation();
  const [isAvailable, setIsAvailable] = useState(false);
  const [appleAuthModule, setAppleAuthModule] = useState<typeof import('expo-apple-authentication') | null>(null);

  useEffect(() => {
    async function check() {
      if (Platform.OS === 'web') {
        if (isApplePlatform()) {
          setIsAvailable(true);
        }
        return;
      }
      if (Platform.OS !== 'ios') return;
      try {
        const mod = await import('expo-apple-authentication');
        setAppleAuthModule(mod);
        const available = await mod.isAvailableAsync();
        setIsAvailable(available);
      } catch (error) {
        console.error('[AppleSignInButton] Error loading module:', error);
      }
    }
    check();
  }, []);

  if (!isAvailable) return null;
  if (Platform.OS !== 'web' && (!appleAuthModule || !appleAuthModule.AppleAuthenticationButton)) return null;

  return (
    <TouchableOpacity
      {...webDataAttrs}
      style={[styles.button, style]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.85}
    >
      <View style={styles.inner} pointerEvents="none">
        <Text {...webIconAttrs} style={styles.icon}>{'\uF8FF'}</Text>
        <Text {...webLabelAttrs} style={styles.label}>{t('otp.continueWithApple')}</Text>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  button: {
    backgroundColor: '#000000',
    borderRadius: 11,
    paddingVertical: 14,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    gap: 10,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
        isolation: 'isolate',
        colorScheme: 'only dark',
        forcedColorAdjust: 'none',
        WebkitAppearance: 'none',
        backgroundImage: 'linear-gradient(#000000, #000000)',
      } as unknown as ViewStyle,
      default: {},
    }),
  },
  inner: {
    backgroundColor: '#000000',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    alignSelf: 'stretch',
  },
  icon: {
    fontSize: 18,
    color: '#FFFFFF',
  },
  label: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: typography.weight.semibold,
  },
});

export default AppleSignInButton;
